import React, { useEffect, useState, useMemo } from "react";
import { Input, Container, Card, CardBody } from "reactstrap";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import AdminHeader from "./Header";
import { MessageCircle, Search, Clock, ChevronRight } from "lucide-react";

// const BASE = "http://localhost/business_community/";
const BASE = "https://qnexus.in/community/";
const FALLBACK_AVATAR = "https://ui-avatars.com/api/?background=1e3a8a&color=fff&name=";

const AdminChatList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [list, setList] = useState([]);
    const [search, setSearch] = useState("");

    useEffect(() => { fetchChats(); }, []);

    const fetchChats = async () => {
        dispatch(loadingTrue());
        try {
            const r = await Handler({ method: "get", url: "/admin_chat/list.php" });
            setList(r?.data?.list || []);
        } catch (e) {
            Handler(e);
            setList([]);
        }
        dispatch(loadingFalse());
    };

    const filteredList = useMemo(() => {
        const keyword = search?.toLowerCase()?.trim();
        if (!keyword) return list;
        return list?.filter(i => i?.business_name?.toLowerCase()?.includes(keyword));
    }, [list, search]);

    const openChat = async (chat) => {
        dispatch(loadingTrue());
        try {
            /* 1. Start or get existing chat */
            const r = await Handler({
                method: "post",
                url: "/admin_chat/start.php",
                data: { user_id: chat?.business_user_id }
            });

            if (r?.success) {
                /* 2. Mark messages as read BEFORE navigating */
                try {
                    await Handler({
                        method: "post",
                        url: "/admin_chat/mark_read.php",
                        data: { business_user_id: chat?.business_user_id }
                    });
                } catch (_) { /* silent */ }

                /* 3. Navigate — pass BOTH chat_id AND business_user_id */
                navigate("/admin/chat", {
                    state: {
                        chat_id: r.data.chat_id,
                        business_user_id: chat?.business_user_id,
                        other_business: {
                            business_name: chat?.business_name,
                            logo: chat?.logo
                        }
                    }
                });
            }
        } catch (e) {
            Handler(e);
        }
        dispatch(loadingFalse());
    };

    return (
        <div className="bg-soft-blue min-vh-100">
            <AdminHeader />
            <Container py="4" className="mt-4 pb-5">

                <div className="d-flex align-items-center mb-4">
                    <div className="icon-box-blue me-3 shadow-sm">
                        <MessageCircle size={22} />
                    </div>
                    <div>
                        <h3 className="fw-bold text-navy mb-0">Direct Messages</h3>
                        <p className="text-muted small mb-0">Manage communication with business profiles</p>
                    </div>
                </div>

                <Card className="border-0 shadow-sm mb-4 rounded-4">
                    <CardBody className="py-3">
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                            <h5 className="mb-0 fw-bold text-navy">Active Conversations</h5>
                            <div className="input-group input-group-sm custom-input-group" style={{ maxWidth: "300px" }}>
                                <span className="input-group-text bg-light border-end-0 text-muted">
                                    <Search size={14} />
                                </span>
                                <Input
                                    className="bg-light border-start-0 shadow-none"
                                    placeholder="Search by business name..."
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                />
                            </div>
                        </div>
                    </CardBody>
                </Card>

                <div className="chat-container shadow-sm border-0">
                    {filteredList?.length > 0 ? filteredList.map(chat => {
                        const businessName = chat?.business_name || "Unknown Business";
                        return (
                            <div
                                key={chat?.business_user_id}
                                className="chat-item d-flex align-items-center gap-3 p-3 border-bottom"
                                onClick={() => openChat(chat)}
                            >
                                <div className="position-relative">
                                    <img
                                        src={chat?.logo ? BASE + chat.logo : `${FALLBACK_AVATAR}${encodeURIComponent(businessName)}`}
                                        alt=""
                                        width="55"
                                        height="55"
                                        className="rounded-circle border-2 border-white shadow-sm chat-avatar"
                                    />
                                    <span className="position-absolute bottom-0 end-0 bg-success border border-2 border-white rounded-circle"
                                        style={{ width: 12, height: 12 }}></span>
                                </div>

                                <div className="flex-grow-1 overflow-hidden">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <span className="fw-bold text-navy text-capitalize mb-0">{businessName}</span>
                                        <span className="text-muted x-small d-flex align-items-center">
                                            <Clock size={12} className="me-1" />
                                            {chat?.last_message_at ? moment(chat.last_message_at).fromNow() : "No date"}
                                        </span>
                                    </div>

                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="text-muted small text-truncate pe-3" style={{ maxWidth: "85%" }}>
                                            {chat?.last_message || "Start a new conversation..."}
                                        </div>
                                        <ChevronRight size={16} className="text-light-blue" />
                                    </div>
                                </div>
                            </div>
                        );
                    }) : (
                        <div className="bg-white text-center text-muted py-5 rounded-4">
                            <MessageCircle size={40} className="mb-3 opacity-20" />
                            <h5>{search ? "No matches found" : "Inbox is empty"}</h5>
                            <p className="small px-4">
                                {search
                                    ? "Try searching for a different business name."
                                    : "No conversations yet. Start one from a business profile."}
                            </p>
                        </div>
                    )}
                </div>

            </Container>

            <style>{`
                .bg-soft-blue { background-color: #f0f4f8; }
                .text-navy { color: #1a365d; }
                .bg-navy { background-color: #1e3a8a; }
                .x-small { font-size: 0.72rem; }
                .text-light-blue { color: #cbd5e0; }
                .icon-box-blue {
                    background: #1e3a8a;
                    color: white;
                    padding: 10px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .custom-input-group .form-control:focus {
                    background-color: #fff;
                    border-color: #1e3a8a;
                    box-shadow: none;
                }
                .chat-container {
                    background: white;
                    border-radius: 15px;
                    overflow: hidden;
                }
                .chat-item {
                    transition: all 0.2s ease;
                    cursor: pointer;
                    background: white;
                }
                .chat-item:last-child { border-bottom: none !important; }
                .chat-item:hover {
                    background-color: #f8fafc;
                    transform: translateX(4px);
                }
                .chat-item:hover .text-navy { color: #1e3a8a !important; }
                .chat-avatar { object-fit: cover; }
                .rounded-4 { border-radius: 1rem !important; }
                .opacity-20 { opacity: 0.2; }
            `}</style>
        </div>
    );
};

export default AdminChatList;